if (!customElements.get('ulviva-product-gallery')) {
  customElements.define('ulviva-product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.unsubscribe?.();
      this.abort = new AbortController();
      this.variantLoading = false;
      this.removeAttribute('data-variant-loading');
      this.addEventListener('click', this.onClick.bind(this), { signal: this.abort.signal });
      this.addEventListener('error', this.onImageError.bind(this), { signal: this.abort.signal, capture: true });
      this.closest('product-info')?.addEventListener('change', event => {
        if (!event.target.closest('variant-selects')) return;
        this.beginVariantLoad(event.target.value);
      }, { signal: this.abort.signal });
      this.bindGallery();
      const initialImage = this.querySelector('.uv-product-gallery__stage img');
      if (initialImage?.complete && initialImage.naturalWidth === 0) this.showImageError();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== this.dataset.section) return;
          const updated = data.html?.querySelector('ulviva-product-gallery');
          if (updated) this.updateImage(updated);
        });
      }
    }
    beginVariantLoad(title) {
      this.viewVersion = (this.viewVersion || 0) + 1;
      this.updateVersion = (this.updateVersion || 0) + 1;
      this.variantLoading = true;
      this.setAttribute('data-variant-loading', 'true');
      this.setAttribute('aria-busy', 'true');
      this.querySelector('dialog')?.close();
      this.querySelectorAll('[data-uv-view]').forEach(a => a.setAttribute('aria-disabled', 'true'));
      this.loading(title, this.dataset.loadingText);
    }
    loading(title, message) {
      this.querySelector('.uv-product-gallery__loading')?.remove();
      const status = document.createElement('p');
      status.className = 'uv-product-gallery__loading';
      status.setAttribute('role', 'status');
      status.textContent = [title, message].filter(Boolean).join(' · ');
      this.querySelector('.uv-product-gallery__stage')?.append(status);
    }
    imageFor(view, root = this) {
      const image = new Image();
      image.sizes = root.dataset.sizes;
      if (view.dataset.uvView !== 'label') image.srcset = view.dataset.src + ' 900w, ' + view.dataset.retina + ' 1600w';
      image.src = view.dataset.src;
      return image;
    }
    applyView(root, view, image) {
      const stage = root.querySelector('.uv-product-gallery__stage img');
      stage.sizes = image.sizes;
      stage.srcset = image.srcset;
      stage.src = image.src;
      stage.alt = root.querySelector('.uv-product-gallery__caption').textContent.trim() + ' · ' + view.dataset.title;
      root.dataset.view = view.dataset.uvView;
      const views = [...root.querySelectorAll('[data-uv-view]')];
      views.forEach(a => a.setAttribute('aria-current', String(a === view)));
      root.querySelector('[data-uv-zoom]').href = view.dataset.zoom;
      const large = root.querySelector('[data-zoom-src]');
      large.removeAttribute('src');
      large.dataset.zoomSrc = view.dataset.zoom;
      large.alt = stage.alt;
      const link = root.querySelector('.uv-product-gallery__zoom-error a');
      if (link) link.href = view.dataset.zoom;
      const index = views.indexOf(view) + 1;
      root.querySelector('[data-uv-position]').textContent = view.dataset.title + ' · ' + index + ' / ' + views.length;
      root.querySelector('.uv-product-gallery__view-counter').textContent = String(index).padStart(2, '0') + ' / ' + String(views.length).padStart(2, '0');
      root.querySelectorAll('.uv-product-gallery__error,.uv-product-gallery__zoom-error').forEach(e => e.hidden = true);
      root.removeAttribute('data-image-failed');
    }
    async updateImage(updated) {
      const requestedView = this.dataset.view || 'front';
      this.beginVariantLoad(updated.querySelector('.uv-product-gallery__caption')?.textContent.trim());
      const version = this.updateVersion = (this.updateVersion || 0) + 1;
      const view = updated.querySelector('[data-uv-view="' + requestedView + '"]') || updated.querySelector('[data-uv-view="front"]');
      const next = view ? this.imageFor(view, updated) : updated.querySelector('.uv-product-gallery__stage img');
      let failed = false;
      try { await next?.decode(); } catch { failed = true; }
      if (!this.isConnected || version !== this.updateVersion) return;
      if (view && next) this.applyView(updated, view, next);
      this.innerHTML = updated.innerHTML;
      this.variantLoading = false;
      this.removeAttribute('data-variant-loading');
      ['loadingText', 'viewLoadingText', 'format', 'sizes', 'view'].forEach(key => this.dataset[key] = updated.dataset[key]);
      this.removeAttribute('aria-busy');
      this.removeAttribute('data-image-failed');
      this.bindGallery();
      if (failed) this.showImageError();
    }
    bindGallery() {
      this.partsAbort?.abort();
      this.partsAbort = new AbortController();
      const signal = this.partsAbort.signal;
      const viewport = this.querySelector('.uv-product-gallery__zoom-viewport');
      const dialog = this.querySelector('dialog');
      const stage = this.querySelector('.uv-product-gallery__stage');
      const trackSwipe = element => {
        element?.addEventListener('pointerdown', event => {
          if (event.pointerType === 'mouse' || dialog.open && dialog.classList.contains('is-detail')) return;
          this.swipe = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now() };
        }, { signal, passive: true });
        element?.addEventListener('pointerup', event => {
          const start = this.swipe;this.swipe = null;
          if (!start || start.id !== event.pointerId) return;
          const dx = event.clientX - start.x, dy = event.clientY - start.y;
          if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.7 && performance.now() - start.time < 800) {
            this.suppressClickUntil = performance.now() + 400;
            this.step(dx < 0 ? 1 : -1);
          }
        }, { signal, passive: true });
        element?.addEventListener('pointercancel', () => this.swipe = null, { signal, passive: true });
      };
      trackSwipe(stage);trackSwipe(viewport);
      viewport?.addEventListener('pointerdown', event => {
        if (!dialog.classList.contains('is-detail') || event.pointerType !== 'mouse' || event.button !== 0) return;
        this.pan = { id: event.pointerId, x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
        viewport.setPointerCapture(event.pointerId);
        viewport.classList.add('is-panning');event.preventDefault();
      }, { signal });
      viewport?.addEventListener('pointermove', event => {
        if (!this.pan || this.pan.id !== event.pointerId) return;
        viewport.scrollLeft = this.pan.left + this.pan.x - event.clientX;
        viewport.scrollTop = this.pan.top + this.pan.y - event.clientY;
      }, { signal });
      const endPan = () => { this.pan = null;viewport?.classList.remove('is-panning'); };
      viewport?.addEventListener('pointerup', endPan, { signal });
      viewport?.addEventListener('pointercancel', endPan, { signal });
      dialog?.addEventListener('keydown', event => {
        if (dialog.classList.contains('is-detail') && event.target === viewport) {
          const step = event.shiftKey ? 220 : 70;
          const arrows = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
          if (arrows[event.key]) { event.preventDefault();viewport.scrollBy(...arrows[event.key]); }
          if (event.key === 'Home') { event.preventDefault();viewport.scrollTo(0, 0); }
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();this.step(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, { signal });
      this.querySelector('.uv-product-gallery__views')?.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const views = [...this.querySelectorAll('[data-uv-view]')];
        let i = views.indexOf(event.target.closest('[data-uv-view]'));
        i = event.key === 'Home' ? 0 : event.key === 'End' ? views.length - 1 : (i + (event.key === 'ArrowRight' ? 1 : -1) + views.length) % views.length;
        views[i]?.focus();this.selectView(views[i]);
      }, { signal });
      dialog?.addEventListener('close', () => {
        endPan();document.documentElement.classList.remove('uv-product-zoom-open');
        if (!this.variantLoading) this.querySelector('[data-uv-zoom]')?.focus({ preventScroll: true });
      }, { signal });
    }
    setDetail(expanded) {
      const dialog = this.querySelector('dialog');
      dialog.classList.toggle('is-detail', expanded);
      const button = dialog.querySelector('[data-uv-detail]');
      button.setAttribute('aria-pressed', String(expanded));
      button.textContent = expanded ? button.dataset.zoomOut : button.dataset.zoomIn;
      const viewport = dialog.querySelector('.uv-product-gallery__zoom-viewport');
      if (expanded) {
        viewport.focus({ preventScroll: true });
        requestAnimationFrame(() => viewport.scrollTo((viewport.scrollWidth - viewport.clientWidth) / 2, (viewport.scrollHeight - viewport.clientHeight) / 2));
      } else viewport.scrollTo(0, 0);
    }
    async loadZoom() {
      const image = this.querySelector('[data-zoom-src]');
      const dialog = this.querySelector('dialog');
      dialog.setAttribute('aria-busy', 'true');
      image.src = image.dataset.zoomSrc;
      const src = image.src;
      try { await image.decode(); } catch {
        if (image.src === src) this.querySelector('.uv-product-gallery__zoom-error').hidden = false;
      } finally { if (image.src === src) dialog.removeAttribute('aria-busy'); }
    }
    onClick(event) {
      if (performance.now() < (this.suppressClickUntil || 0)) { event.preventDefault();return; }
      const view = event.target.closest('[data-uv-view]');
      if (view) { event.preventDefault();this.selectView(view);return; }
      const dialog = this.querySelector('dialog');
      const step = event.target.closest('[data-uv-step]');
      if (step) { this.step(Number(step.dataset.uvStep));return; }
      if (event.target.closest('[data-uv-zoom]') && typeof dialog?.showModal === 'function') {
        event.preventDefault();
        if (this.variantLoading) return;
        this.setDetail(false);this.loadZoom();dialog.showModal();
        document.documentElement.classList.add('uv-product-zoom-open');
      }
      if (event.target.closest('[data-uv-detail]')) this.setDetail(!dialog.classList.contains('is-detail'));
      if (event.target.closest('[data-uv-retry]')) this.selectView(this.querySelector('[data-uv-view][aria-current="true"]') || this.querySelector('[data-uv-view]'));
      if (event.target.closest('[data-uv-close]') || event.target === dialog) dialog?.close();
    }
    step(direction) {
      const views = [...this.querySelectorAll('[data-uv-view]')];
      if (!views.length || this.variantLoading) return;
      const current = Math.max(0, views.findIndex(v => v.dataset.uvView === this.dataset.view));
      this.selectView(views[(current + direction + views.length) % views.length]);
    }
    async selectView(view) {
      if (!view || this.variantLoading) return;
      const version = this.viewVersion = (this.viewVersion || 0) + 1;
      const candidate = this.imageFor(view);
      this.setAttribute('aria-busy', 'true');
      this.loading('', this.dataset.viewLoadingText);
      try {
        await candidate.decode();
        if (!this.isConnected || !view.isConnected || !this.contains(view) || version !== this.viewVersion) return;
        this.applyView(this, view, candidate);
        if (this.querySelector('dialog').open) { this.setDetail(false);this.loadZoom(); }
      } catch {
        if (version === this.viewVersion) this.showImageError();
      } finally {
        if (version === this.viewVersion) { this.removeAttribute('aria-busy');this.querySelector('.uv-product-gallery__loading')?.remove(); }
      }
    }
    onImageError(event) {
      if (event.target.matches('.uv-product-gallery__stage img')) this.showImageError();
      if (event.target.matches('[data-zoom-src]')) this.querySelector('.uv-product-gallery__zoom-error').hidden = false;
    }
    showImageError() {
      this.setAttribute('data-image-failed', 'true');
      const error = this.querySelector('.uv-product-gallery__error');
      if (error) error.hidden = false;
    }
    disconnectedCallback() {
      this.unsubscribe?.();this.abort?.abort();this.partsAbort?.abort();
      this.updateVersion = (this.updateVersion || 0) + 1;this.viewVersion = (this.viewVersion || 0) + 1;
      document.documentElement.classList.remove('uv-product-zoom-open');
    }
  });
}

if (!customElements.get('ulviva-sticky-purchase')) {
  customElements.define('ulviva-sticky-purchase', class extends HTMLElement {
    connectedCallback() {
      this.cleanup();
      this.controller = new AbortController();
      this.owner = this.closest('product-info');
      this.form = this.owner?.querySelector('product-form form');
      this.nativeButton = this.form?.querySelector('[type="submit"]');
      if (!this.nativeButton) return;
      this.media = matchMedia('(max-width: 749px)');
      this.addEventListener('click', event => {
        if (event.target.closest('[data-uv-buy-submit]') && !this.nativeButton.disabled && this.nativeButton.getAttribute('aria-disabled') !== 'true') {
          this.submittedFromBar = true;
          this.nativeButton.click();
        }
        if (event.target.closest('[data-uv-buy-edit]')) {
          event.preventDefault();
          const controls = this.owner.querySelector('variant-selects') || this.nativeButton;
          controls.scrollIntoView({block:'center',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
          const first = controls.querySelector('input:checked, select') || this.nativeButton;
          first.focus({preventScroll:true});
        }
      }, {signal:this.controller.signal});
      this.owner.addEventListener('change', () => this.sync(), {signal:this.controller.signal});
      this.owner.addEventListener('input', () => this.sync(), {signal:this.controller.signal});
      this.media.addEventListener('change', () => this.visibility(), {signal:this.controller.signal});
      window.addEventListener('resize', () => this.positionAbovePreview(), {signal:this.controller.signal});
      const watchPreview = () => {
        const wrapper = document.getElementById('PBarNextFrameWrapper');
        if (!wrapper) return false;
        this.previewWrapper = wrapper;
        this.previewInsertion?.disconnect();
        this.previewResize = new ResizeObserver(() => this.positionAbovePreview());
        this.previewResize.observe(wrapper);
        this.previewAttributes = new MutationObserver(() => this.positionAbovePreview());
        this.previewAttributes.observe(wrapper,{attributes:true,attributeFilter:['style','class','hidden']});
        this.positionAbovePreview();
        return true;
      };
      if (!watchPreview() && window.Shopify?.theme?.role === 'unpublished') {
        this.previewInsertion = new MutationObserver(watchPreview);
        this.previewInsertion.observe(document.body,{childList:true,subtree:true});
      }
      this.observer = new IntersectionObserver(entries => {
        this.pastPurchase = entries[0].boundingClientRect.bottom <= 0;
        this.visibility();
      });
      this.observer.observe(this.owner);
      this.mutation = new MutationObserver(() => this.sync());
      this.mutation.observe(this.nativeButton,{attributes:true,attributeFilter:['disabled','aria-disabled','class']});
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({data}) => {
          if (String(data.sectionId) !== this.dataset.section) return;
          const fresh = data.html?.querySelector('ulviva-sticky-purchase');
          if (fresh) this.innerHTML = fresh.innerHTML;
          this.sync();
        });
      }
      this.sync();
    }
    sync() {
      const button = this.querySelector('[data-uv-buy-submit]');
      if (button) {
        button.disabled = this.nativeButton.disabled || this.nativeButton.getAttribute('aria-disabled') === 'true';
        button.setAttribute('aria-busy',String(this.nativeButton.classList.contains('loading')));
      }
      const quantity = this.owner.querySelector('input[name="quantity"]')?.value || '1';
      const label = this.querySelector('[data-uv-buy-quantity]');
      if (label) label.textContent = quantity;
      if (this.submittedFromBar && !this.nativeButton.classList.contains('loading')) {
        this.submittedFromBar = false;
        const error = this.owner.querySelector('.product-form__error-message-wrapper:not([hidden])');
        if (error?.textContent.trim()) error.scrollIntoView({block:'center',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
      }
    }
    visibility() {
      this.hidden = !(this.media?.matches && this.pastPurchase);
      document.documentElement.classList.toggle('uv-mobile-purchase-visible',!this.hidden);
      this.positionAbovePreview();
    }
    positionAbovePreview() {
      const rect = this.previewWrapper?.getBoundingClientRect();
      const offset = rect?.width > 0 && rect.height > 0 && rect.bottom >= innerHeight - 2 ? Math.max(0,innerHeight-rect.top) : 0;
      this.style.setProperty('--uv-preview-offset',`${offset}px`);
    }
    cleanup() {
      this.controller?.abort();this.unsubscribe?.();this.observer?.disconnect();this.mutation?.disconnect();
      this.previewInsertion?.disconnect();this.previewResize?.disconnect();this.previewAttributes?.disconnect();
      document.documentElement.classList.remove('uv-mobile-purchase-visible');
    }
    disconnectedCallback() { this.cleanup(); }
  });
}
